import type { Clip, LogoConfig, Reframe, StyleDna } from '../types'
import { clipKept, totalDuration } from './ranges'
import { canvasSize, computeDraw, getFormat, type FormatKey } from './formats'

export interface ExportOpts {
  logo: LogoConfig
  formatKey: FormatKey
  reframe: Reframe
  style: StyleDna
  studioAudio: boolean
}

function pickMime(): string {
  const candidates = [
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ]
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m
  }
  return 'video/webm'
}

function loadSrc(video: HTMLVideoElement, url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onReady = () => {
      video.removeEventListener('loadeddata', onReady)
      resolve()
    }
    video.addEventListener('loadeddata', onReady)
    video.onerror = () => reject(new Error('Không nạp được clip khi xuất.'))
    video.src = url
    video.load()
  })
}

function seekTo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      resolve()
    }
    video.addEventListener('seeked', onSeeked)
    video.currentTime = t
  })
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Không nạp được logo.'))
    img.src = url
  })
}

/** CSS-style filter cho grade màu (Style DNA). */
function gradeFilter(style: StyleDna): string {
  if (!style.apply) return 'none'
  const g = style.grade
  return `brightness(${g.brightness}) contrast(${g.contrast}) saturate(${g.saturate})`
}

/** Lớp phủ tông ấm-lạnh. */
function warmthFill(style: StyleDna): string | null {
  if (!style.apply || Math.abs(style.grade.warmth) < 0.02) return null
  const w = style.grade.warmth
  return w > 0
    ? `rgba(255,150,40,${Math.min(0.35, w * 0.3)})`
    : `rgba(40,120,255,${Math.min(0.35, -w * 0.3)})`
}

/** Xuất dự án ở MỘT tỉ lệ. */
export async function exportFormat(
  clips: Clip[],
  opts: ExportOpts,
  onProgress?: (ratio: number) => void,
): Promise<{ blob: Blob; ext: string }> {
  if (clips.length === 0) throw new Error('Chưa có clip nào để xuất.')

  const { logo, formatKey, reframe, style, studioAudio } = opts
  const fmt = getFormat(formatKey)
  const { w: CW, h: CH } = canvasSize(fmt, { width: clips[0].width, height: clips[0].height })

  const logoImg = logo.url ? await loadImage(logo.url) : null

  const canvas = document.createElement('canvas')
  canvas.width = CW
  canvas.height = CH
  const ctx = canvas.getContext('2d')!
  const canvasStream = canvas.captureStream(30)

  const video = document.createElement('video')
  video.muted = false
  video.playsInline = true

  const AC: typeof AudioContext =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  const audioCtx = new AC()
  const srcNode = audioCtx.createMediaElementSource(video)
  const dest = audioCtx.createMediaStreamDestination()

  // Studio audio: high-pass khử ù + nén động + tăng bù.
  if (studioAudio) {
    const hp = audioCtx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 85
    const comp = audioCtx.createDynamicsCompressor()
    comp.threshold.value = -24
    comp.ratio.value = 4
    comp.attack.value = 0.003
    comp.release.value = 0.25
    const makeup = audioCtx.createGain()
    makeup.gain.value = 1.4
    srcNode.connect(hp)
    hp.connect(comp)
    comp.connect(makeup)
    makeup.connect(dest)
  } else {
    srcNode.connect(dest)
  }

  const stream = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...dest.stream.getAudioTracks(),
  ])
  const mime = pickMime()
  const rec = new MediaRecorder(stream, { mimeType: mime })
  const chunks: Blob[] = []
  rec.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data)
  }
  const done = new Promise<Blob>((resolve) => {
    rec.onstop = () => resolve(new Blob(chunks, { type: mime }))
  })

  const filter = gradeFilter(style)
  const tint = warmthFill(style)

  const drawLogo = () => {
    if (!logoImg) return
    const lw = CW * logo.scale
    const lh = lw * (logoImg.height / logoImg.width)
    const m = CW * logo.margin
    const x = logo.pos.includes('l') ? m : CW - lw - m
    const y = logo.pos.includes('t') ? m : CH - lh - m
    ctx.globalAlpha = logo.opacity
    ctx.drawImage(logoImg, x, y, lw, lh)
    ctx.globalAlpha = 1
  }

  const grandTotal = clips.reduce((a, c) => a + totalDuration(clipKept(c)), 0)

  rec.start(100)
  let elapsed = 0

  for (const clip of clips) {
    await loadSrc(video, clip.url)
    const ranges = clipKept(clip)
    const draw = computeDraw(clip, CW, CH, fmt, reframe)

    for (const r of ranges) {
      await seekTo(video, r.start)
      await video.play().catch(() => {})
      await new Promise<void>((resolve) => {
        const frame = () => {
          ctx.filter = 'none'
          ctx.fillStyle = '#000'
          ctx.fillRect(0, 0, CW, CH)
          ctx.filter = filter // grade màu áp lên khung video
          ctx.drawImage(video, draw.x, draw.y, draw.w, draw.h)
          ctx.filter = 'none'
          if (tint) {
            ctx.fillStyle = tint
            ctx.fillRect(0, 0, CW, CH)
          }
          drawLogo()
          onProgress?.(
            Math.min(1, grandTotal ? (elapsed + (video.currentTime - r.start)) / grandTotal : 0),
          )
          if (video.currentTime >= r.end - 0.03 || video.paused || video.ended) {
            resolve()
            return
          }
          requestAnimationFrame(frame)
        }
        requestAnimationFrame(frame)
      })
      video.pause()
      elapsed += r.end - r.start
    }
  }

  rec.stop()
  const blob = await done
  await audioCtx.close()
  onProgress?.(1)

  const ext = mime.startsWith('video/mp4') ? 'mp4' : 'webm'
  return { blob, ext }
}
